import { FC, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import { Box, Stack } from '@mui/material';
import DataTable from '@/components/data-table';
import PageHeader from '@/components/page/header';
import { DtColumn, DtColumnGroup, DtFilter, DtPage } from '@/components/data-table/helpers';
import { getPublicServices } from '@/lib/services/public-service.service';
import DownloadMenu from './download-menu';
import FeatureCheckmark from './feature-checkmark';
import { PublicServiceModel } from '@/lib/models/public-service/public-service.model';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { AppDataQueryKey, QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import StatusChip from '@/components/chip/status-chip';
import RiskChip from '@/components/chip/risk-chip';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import IntercomButton from '@/components/intercom/intercom-button';
import ServiceLink from '@/components/service-link';
import useDrawer from '@/hooks/use-drawer.hook';
import useAppData from '@/hooks/use-app-data.hook';
import { SignalOpts } from '@/lib/models/public-service';
import { PsFeatureOpts } from '@/lib/models/public-service/support';

const featureColumnProps: Partial<DtColumn> = {
  flex: 0.5,
  sortable: false,
  align: 'center',
};

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 1.5, sortable: true },
  { name: 'risk', label: 'Risk Level', flex: 0.6, sortable: true },

  // feature matrix columns
  { name: 'request', label: 'Request', ...featureColumnProps },
  { name: 'response', label: 'Response', ...featureColumnProps },
  { name: 'license', label: 'License', ...featureColumnProps },
  { name: 'fileUpload', label: 'File Upload', ...featureColumnProps },
  { name: 'mcp', label: 'MCP', ...featureColumnProps },

  { name: 'action', label: '', flex: 0.6, sortable: false },
];

const columnGroups: DtColumnGroup[] = [
  {
    groupId: 'intercept-support',
    headerName: 'Intercept Support',
    children: ['request', 'response', 'license', 'fileUpload', 'mcp', 'prompt'],
  },
];

const filter: DtFilter = {
  keyword: '',
  fields: [
    {
      columnName: 'riskSignals',
      label: 'Risk Signals',
      control: 'select-multi',
      highlander: true,
      options: SignalOpts,
    },
    {
      columnName: 'riskLevel',
      label: 'Risk Level',
      control: 'select-multi',
      highlander: true,
      options: [
        { label: 'Low', value: 'LOW' },
        { label: 'Medium', value: 'MEDIUM' },
        { label: 'High', value: 'HIGH' },
      ],
    },
    {
      columnName: 'interceptFeatures',
      label: 'Intercept Support',
      control: 'select-multi',
      highlander: true,
      options: PsFeatureOpts,
    },
  ],
  values: [],
};

const PublicServicesPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');

  const publicServiceQuery = useAppData<PublicServiceModel[]>(AppDataQueryKey.PublicServices);
  const { data: publicServices } = publicServiceQuery;

  const handleView = (id: string) => {
    setSelectedRowId(id || '');
    openDrawer('public-service', {
      id,
      onClose: () => {
        setSelectedRowId('');
      },
    });
  };

  const loadData = async (pageParams: DtPage, filter?: DtFilter) => {
    const { page, pageSize, sort } = pageParams;
    const dataset = await getPublicServices(page, pageSize, sort, filter, publicServices);

    return {
      ...dataset,
      rows: dataset.rows.map((publicService) => {
        const { id, companyName, support, riskLevel, status, promptGuardStatus } = publicService;
        const notAvailable = status !== 'available';
        const NameEl = (
          <ServiceLink
            service={publicService}
            subtext={companyName.value}
            size="small"
            iconSize={32}
            action="none"
          />
        );

        const { featureMatrix } = support;
        const hasRequest = !!featureMatrix.request?.enabled;

        return {
          id,
          name: notAvailable ? (
            <Stack gap={1} direction="row" alignItems="center">
              {NameEl}
              <StatusChip value={status} size="small" />
            </Stack>
          ) : (
            NameEl
          ),
          risk: <RiskChip value={riskLevel} size="small" />,
          request: (
            <FeatureCheckmark
              enabled={hasRequest && promptGuardStatus === 'enabled'}
              partial={hasRequest && promptGuardStatus === 'partial'}
              label="Request Intercept"
            />
          ),
          response: (
            <FeatureCheckmark
              enabled={!!featureMatrix.response?.enabled}
              label="Response Intercept"
            />
          ),
          license: (
            <FeatureCheckmark
              enabled={!!featureMatrix.license?.enabled}
              label="License Intercept"
            />
          ),
          fileUpload: (
            <FeatureCheckmark enabled={!!featureMatrix.fileUpload?.enabled} label="File Upload" />
          ),
          mcp: <FeatureCheckmark enabled={!!featureMatrix.mcp?.enabled} label="MCP" />,
          action: <OpenDrawerButton onClick={() => handleView(id)} />,
        };
      }),
    };
  };

  return (
    <Page title="Public Services" query={publicServiceQuery}>
      <PageHeader>
        <PageBlurb>
          <Stack gap={2}>
            <Box>
              Explore our complete directory of supported public services. Get detailed service
              information and learn how F5 Workforce AI Security supports each service to help you
              make informed policy decisions.
            </Box>
            <Box>
              <IntercomButton
                intercomArticle="public-services"
                label="Learn About the Public Service Catalog"
                flatButton
              />
            </Box>
          </Stack>
        </PageBlurb>
        <PageTools>
          <DownloadMenu />
        </PageTools>
      </PageHeader>
      <DomSize>
        <DataTable
          queryKey={QueryKey.PublicServicesDataset}
          searchPlaceholder="Search by name"
          columns={columns}
          onLoad={loadData}
          search
          filter={filter}
          rowHeight={86}
          selectedRowId={selectedRowId}
          columnGroups={columnGroups}
        />
      </DomSize>
    </Page>
  );
};

export default PublicServicesPage;
